import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { debounce } from '@mui/material/utils';
import { BoostIcon } from '@openx/components-core/icons/BoostIcon/BoostIcon.tsx';
import { CustomIcon } from '@openx/components-core/icons/CustomIcon/CustomIcon.tsx';
import { FireIcon } from '@openx/components-core/icons/FireIcon/FireIcon.tsx';
import { SparkleIcon } from '@openx/components-core/icons/SparkleIcon/SparkleIcon.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import {
  type DealFormData,
  type DealFormDataWithTargeting,
  dealFormField,
  type RboConfig,
  type RboConfigField,
  rboConfigField,
  rboConfigMode,
} from '@openx/types/deal.ts';
import { rboConfigTactic } from '@openx/types/rbo.ts';
import { type UseWatchName, useWatchManyDebounce } from '@openx/utils/hooks/useWatchDebounce.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { GroupDealRboAlert } from '../../../components/DealGroup/GroupDealRboAlert.tsx';
import { ResultsByOpenXReadonly } from '../../../components/ResultsByOpenX/ResultsByOpenXReadonly.tsx';
import { useRboFields } from '../../../hooks/useRboFields.ts';
import { getRboLabels, getRboPlaceholders } from '../../utils/dealFormUtils.ts';
import { CustomSectionFields } from './CustomSectionFields.tsx';
import { PerformanceQualityFilter } from './PerformanceQualityFilter.tsx';

type Props = {
  isEdit: boolean;
};

export const ResultsByOpenXSection = ({ isEdit }: Props): ReactNode => {
  const { t } = useTranslation();
  const locationState = useLocation().state;

  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const isRboAdmin = useMemo(
    () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_RBO_CONFIG_ADMIN_WRITE)),
    [isAllowed]
  );

  const rboLabels = useMemo(() => getRboLabels(t), [t]);
  const rboPlaceholders = useMemo(() => getRboPlaceholders(t), [t]);
  const {
    setValue,
    getValues,
    formState: { errors, defaultValues },
    trigger,
  } = useFormContext<DealFormDataWithTargeting>();

  const initialRboConfigForToggleRef = useRef<RboConfig | null | undefined>(undefined);

  useEffect(() => {
    if (initialRboConfigForToggleRef.current !== undefined) {
      return;
    }
    const snapshot = getValues(dealFormField.RBO_CONFIG) ?? defaultValues?.rbo_config;
    if (snapshot == null) {
      return;
    }
    initialRboConfigForToggleRef.current = structuredClone(snapshot);
  }, [defaultValues?.rbo_config, getValues]);

  const observableRboConfig: RboConfig = useWatch({
    name: dealFormField.RBO_CONFIG,
  });

  const observableModelGoal = useWatch({
    name: `${dealFormField.RBO_CONFIG}.${rboConfigField.MODEL_GOAL}`,
  });

  const names: UseWatchName<DealFormData> = useMemo(
    () => (isEdit ? [dealFormField.DEAL_ID] : [dealFormField.CUSTOM_DEAL_ID, dealFormField.EXTERNAL_DEAL_ID]),
    [isEdit]
  );
  const observableDealIds = useWatchManyDebounce({
    name: names,
  });

  const [hasRboInitially] = useState(!!defaultValues?.[dealFormField.RBO_CONFIG]);
  const [resultsByOpenXChecked, setResultsByOpenXChecked] = useState(hasRboInitially);

  const group =
    useWatch<DealFormDataWithTargeting, typeof dealFormField.METADATA_GROUP_DEAL>({
      name: dealFormField.METADATA_GROUP_DEAL,
    }) || [];

  const currentDealId = observableDealIds.find(dealId => !!dealId) ?? '';
  const dealGroupLoading = !!currentDealId && group.length === 0;

  const { groupDealWithRbo, groupHasOtherDealWithRbo } = useMemo(() => {
    const groupDealWithRbo = group.find(({ rboConfig }) => !!rboConfig);

    return {
      groupDealWithRbo,
      groupHasOtherDealWithRbo: !!groupDealWithRbo && !hasRboInitially,
    };
  }, [group, hasRboInitially]);

  const isSwitchDisabled = useMemo(
    () => dealGroupLoading || groupHasOtherDealWithRbo,
    [dealGroupLoading, groupHasOtherDealWithRbo]
  );

  const shouldDisableGoalField = useMemo(() => {
    if (!hasRboInitially || !isEdit) {
      return false;
    }

    return !!observableRboConfig?.[rboConfigField.MODEL_GOAL];
  }, [hasRboInitially, isEdit, observableRboConfig?.[rboConfigField.MODEL_GOAL]]);

  const rboFields = useRboFields({ readonly: false });

  const handleClearField = useCallback(
    (field: RboConfigField) => {
      setValue(`${dealFormField.RBO_CONFIG}.${field}`, null, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const debouncedValidation = useMemo(
    () =>
      debounce((field: RboConfigField) => {
        return trigger(`${dealFormField.RBO_CONFIG}.${field}`);
      }),
    [trigger]
  );

  const onSwitchChange = useCallback(() => {
    setResultsByOpenXChecked(checked => {
      if (checked) {
        setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue(
          `${dealFormField.RBO_CONFIG}`,
          initialRboConfigForToggleRef.current ??
            defaultValues?.rbo_config ?? { [rboConfigField.MODE]: rboConfigMode.BOOST },
          { shouldDirty: true, shouldValidate: true }
        );
      }
      return !checked;
    });
  }, [setValue, defaultValues?.rbo_config]);

  const onFilterSwitchChange = useCallback(
    (value: string) => {
      setValue(
        `${dealFormField.RBO_CONFIG}`,
        {
          ...(observableRboConfig ?? defaultValues?.rbo_config ?? {}),
          [rboConfigField.MODE]: value,
        },
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [defaultValues?.rbo_config, observableRboConfig, setValue]
  );

  const { tactic, threshold } = observableRboConfig ?? {};
  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    if (tactic === rboConfigTactic.DYNAMIC) {
      trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.THRESHOLD}`);
    }

    if (tactic === rboConfigTactic.FIXED) {
      trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.HOURLY_TARGET}`);
    }

    trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.CUSTOM_SCORE_NAME}`);
  }, [trigger, tactic, threshold]);

  useEffect(() => {
    if (groupHasOtherDealWithRbo) {
      setResultsByOpenXChecked(false);
      setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [setValue, groupHasOtherDealWithRbo]);

  useEffect(() => {
    if (locationState?.useAsTemplate) {
      setResultsByOpenXChecked(false);
      setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [locationState?.useAsTemplate, setValue]);

  // Clear value from Channel field when it is hidden
  useEffect(() => {
    const modelGoal = observableRboConfig?.[rboConfigField.MODEL_GOAL];

    if (modelGoal === 'conversion') {
      setValue(`${dealFormField.RBO_CONFIG}.${rboConfigField.CHANNEL}`, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [observableRboConfig?.[rboConfigField.MODEL_GOAL], setValue]);

  // Open the switch when rboConfig is set programmatically
  useEffect(() => {
    if (observableRboConfig && !resultsByOpenXChecked) {
      setResultsByOpenXChecked(true);
    }

    if (!observableRboConfig) {
      setResultsByOpenXChecked(false);
    }
  }, [observableRboConfig, resultsByOpenXChecked]);

  const { rbo_config: rboErrors = {} } = errors;

  const isGroupDealWithOtherRbo = groupHasOtherDealWithRbo && groupDealWithRbo;
  const groupDealRboConfig = useMemo(() => groupDealWithRbo?.rboConfig, [groupDealWithRbo?.rboConfig]);
  const rboConfig = useMemo(
    () => (isGroupDealWithOtherRbo ? groupDealRboConfig : observableRboConfig),
    [isGroupDealWithOtherRbo, groupDealRboConfig, observableRboConfig]
  );

  const initialRboMode = useMemo(
    () => defaultValues?.[dealFormField.RBO_CONFIG]?.[rboConfigField.MODE],
    [defaultValues?.[dealFormField.RBO_CONFIG]?.[rboConfigField.MODE]]
  );
  const showCustomMode = useMemo(
    () => isRboAdmin || initialRboMode === rboConfigMode.CUSTOM || !!(isEdit && rboConfig && !rboConfig?.mode),
    [isRboAdmin, initialRboMode, isEdit, rboConfig]
  );

  const customModeChild = useMemo(() => {
    if (!isRboAdmin) {
      return null;
    }

    if (isGroupDealWithOtherRbo) {
      return <ResultsByOpenXReadonly rboConfig={rboConfig} title={t('Custom Settings')} />;
    }

    return (
      <CustomSectionFields
        rboFields={rboFields}
        modelGoal={observableModelGoal}
        rboAdmin={isRboAdmin}
        rboLabels={rboLabels}
        rboPlaceholders={rboPlaceholders}
        setValue={setValue}
        rboErrors={rboErrors as Record<string, unknown>}
        observableRboConfig={observableRboConfig}
        debouncedValidation={debouncedValidation}
        shouldDisableGoalField={shouldDisableGoalField}
        handleClearField={handleClearField}
      />
    );
  }, [
    isRboAdmin,
    isGroupDealWithOtherRbo,
    rboConfig,
    rboFields,
    observableModelGoal,
    rboLabels,
    rboPlaceholders,
    rboErrors,
    observableRboConfig,
    shouldDisableGoalField,
    t,
    setValue,
    debouncedValidation,
    handleClearField,
  ]);

  const performanceQualityFilterRadioOptions = useMemo(
    () => [
      {
        value: rboConfigMode.BOOST,
        dataTest: 'boost-performance-quality-filter-radio',
        helperText: (
          <Trans i18nKey="dealDetailsRboBoostOptionDescription">
            Exclude requests with <strong>the lowest</strong> ad engagement scores.
          </Trans>
        ),
        label: t('Boost'),
        tooltip: null,
        isAllowed: true,
        child: null,
        icon: <BoostIcon height={18} />,
      },
      {
        value: rboConfigMode.MAX,
        dataTest: 'max-performance-quality-filter-radio',
        helperText: (
          <Trans i18nKey="dealDetailsRboMaxOptionDescription">
            Target requests with <strong>the highest</strong> ad engagement scores.
          </Trans>
        ),
        label: t('Max'),
        tooltip: t('Once active, the feature may take up to 48 hours to converge to optimal deal volume & threshold.'),
        isAllowed: true,
        child: null,
        icon: <FireIcon height={18} />,
      },
      {
        value: rboConfigMode.CUSTOM,
        dataTest: 'custom-performance-quality-filter-radio',
        helperText: t('Set custom optimization settings for the feature.'),
        label: t('Custom'),
        tooltip: null,
        isAllowed: showCustomMode,
        child: customModeChild,
        icon: <CustomIcon height={18} />,
      },
    ],
    [t, showCustomMode, customModeChild]
  );

  return (
    <FormSection
      data-test="results-by-openx"
      divider
      id="results-by-openx-section"
      title={t('Engagement Optimization')}
      titleIcon={<SparkleIcon />}
      description={t(
        'Dynamically optimize your deal with AI to include inventory most likely to drive ad engagement, including clicks and video completes.'
      )}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <SwitchField
          label={t('Enable Engagement Optimization')}
          type="checkbox"
          checked={resultsByOpenXChecked || groupHasOtherDealWithRbo}
          onChange={onSwitchChange}
          data-test="toggle-results-by-openx"
          disabled={isSwitchDisabled}
        />
        <Tooltip
          title={t('The feature optimizes for display and video ad engagement metrics (CTR, CPC, VTR, CPCV).')}
          placement="right"
        >
          <IconButton
            size="small"
            data-test="enable-results-by-openx-info"
            aria-label={t('Engagement Optimization info')}
          >
            <InfoRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {isGroupDealWithOtherRbo && (
        <GroupDealRboAlert key={groupDealWithRbo.id} deal={groupDealWithRbo} sx={{ marginBottom: 1, marginTop: 2 }} />
      )}

      <PerformanceQualityFilter
        radioOptions={performanceQualityFilterRadioOptions}
        disabled={!resultsByOpenXChecked || groupHasOtherDealWithRbo}
        onSwitchChange={onFilterSwitchChange}
        value={rboConfig?.[rboConfigField.MODE] ?? (rboConfig && rboConfigMode.CUSTOM)}
      />
    </FormSection>
  );
};
